<script setup lang="ts">
import AsmaItem from "@/components/asma/AsmaItem.vue";
import type { AsmaCategory, AsmaName } from "@/data/asma";
import { computed, nextTick, ref, watch } from "vue";

type CategoryKey = "all" | AsmaCategory;

const props = defineProps<{
  asma: AsmaName[];
  searchQuery: string;
  activeCategory: CategoryKey;
  activeCategoryLabel: string;
}>();

const openId = ref<number | null>(null);

const filteredNames = computed(() => {
  let data = props.asma;

  if (props.activeCategory !== "all") {
    data = data.filter((n) => n.category === props.activeCategory);
  }

  if (props.searchQuery.trim()) {
    const q = props.searchQuery.toLowerCase();
    data = data.filter(
      (n) =>
        n.transliteration.toLowerCase().includes(q) ||
        n.meaning.toLowerCase().includes(q),
    );
  }

  return data;
});
const hasActiveSearch = computed(() => props.searchQuery.trim().length > 0);

watch(() => props.activeCategory, async () => {
  openId.value = null;
  await nextTick();
  window.scrollTo({ top: 0, behavior: "smooth" });
});

function toggleItem(id: number) {
  openId.value = openId.value === id ? null : id;
}

function getCategoryDescription(key: CategoryKey) {
  const map: Record<AsmaCategory, string> = {
    rehmat: "Yeh naam Allah ki rehmat aur maghfirat se mutalliq hain.",
    qudrat: "Yeh naam Allah ki qudrat aur taqat ko bayan karte hain.",
    ilm: "Yeh naam Allah ke ilm aur hikmat ko zahir karte hain.",
    rizq: "Yeh naam rizq aur barkat se mutalliq hain.",
    adal: "Yeh naam insaf aur haq se mutalliq hain.",
    hifazat: "Yeh naam hifazat aur aman dene wale hain.",
    khalq: "Yeh naam takhleeq aur paidaish ko bayan karte hain.",
    zindagi: "Yeh naam zindagi aur maut ke ikhtiyar ko bayan karte hain.",
    azmat: "Yeh naam Allah ki azmat aur jalal ko zahir karte hain.",
  };

  if (key === "all") return "";
  return map[key];
}
</script>

<template>
  <div>
    <div v-if="hasActiveSearch" class="text-sm text-gray-500 dark:text-gray-400 mb-3">
      {{ filteredNames.length }} results
    </div>

    <div v-if="!hasActiveSearch && props.activeCategory !== 'all'" class="mb-6 mt-2">
      <h2 class="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-1">
        {{ props.activeCategoryLabel }} ke Naam
      </h2>
      <p class="text-sm text-gray-500 dark:text-gray-400">
        {{ getCategoryDescription(props.activeCategory) }}
      </p>
    </div>

    <div class="space-y-4">
      <AsmaItem
        v-for="name in filteredNames"
        :key="name.id"
        :name="name"
        :is-open="openId === name.id"
        @toggle="toggleItem"
      />
    </div>
  </div>
</template>
